import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import AuthExperience from '@app/pages/AuthExperience';
import OrgCreatePage from '@app/pages/OrgCreatePage';
import ShellPage from '@app/pages/ShellPage';
import ProfilePage from '@app/pages/ProfilePage';
import SlackConnectPage from '@app/pages/SlackConnectPage';
import IntegrationConnectPage from '@app/pages/IntegrationConnectPage';
import SsoCallbackPage from '@app/pages/SsoCallbackPage';
import InternalTopologyRenderPage from '@app/pages/InternalTopologyRenderPage';
import MockSsoPage from '@app/pages/MockSsoPage';
import TopologyRuntimeOverlayMockPage from '@app/pages/TopologyRuntimeOverlayMockPage';
import OrgOverviewPage from '@app/pages/org/OrgOverviewPage';
import UnifiedIncidentsPage from '@app/pages/org/UnifiedIncidentsPage';
import UnifiedIncidentPage from '@app/pages/org/UnifiedIncidentPage';
import GrafanaExplorerPage from '@app/pages/org/GrafanaExplorerPage';
import AccessIdentityPage from '@app/pages/org/AccessIdentityPage';
import ConnectivitySitesPage from '@app/pages/org/ConnectivitySitesPage';
import PosturePage from '@app/pages/org/PosturePage';
import TopologyPathTracerPage from '@app/pages/org/topology/TopologyPathTracerPage';
import TopologyV2Page from '@app/pages/org/topology/TopologyV2Page';
import InventoryPage from '@app/pages/org/InventoryPage';
import EndpointsPage from '@app/pages/org/EndpointsPage';
import DeviceSshPage from '@app/pages/org/DeviceSshPage';
import DeviceRunningConfigPage from '@app/pages/org/DeviceRunningConfigPage';
import SiteIntelligencePage from '@app/pages/org/SiteIntelligencePage';
import CredentialsPage from '@app/pages/org/CredentialsPage';
import CVEGlobalSummaryPage from '@app/pages/CVEGlobalSummaryPage';
import ChatAgentsPage from '@app/pages/org/ChatAgentsPage';
import AuditActivityPage from '@app/pages/org/AuditActivityPage';
import OrgSettingsPage from '@app/pages/org/OrgSettingsPage';
import KnowledgeUploadPage from '@app/pages/org/KnowledgeUploadPage';
import IntegrationsPage from '@app/pages/org/IntegrationsPage';
import RunbooksPage from '@app/pages/org/runbooks/workspace/RunbooksPage';
import ExecutionPage from '@app/pages/org/runbooks/workspace/ExecutionPage';
import RunbookNewPage from '@app/pages/org/runbooks/RunbookNewPage';
import RunbookDetailPage from '@app/pages/org/runbooks/RunbookDetailPage';
import AcceptInvitePage from '@app/pages/AcceptInvitePage';
import OrgSelectPage from '@app/pages/OrgSelectPage';
import TroubleshootPage from '@app/pages/org/TroubleshootPage';
import ApiDocsPage from '@app/pages/org/ApiDocsPage';
import ChangeManagementPage from '@app/pages/org/ChangeManagementPage';
import PingMeshPage from '@app/pages/org/PingMeshPage';
import FlowAnalyticsPage from '@app/pages/org/FlowAnalyticsPage';


const LegacyIncidentsListRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/app/incidents', search: location.search }} replace state={location.state} />;
};

const LegacyIncidentDetailRedirect = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const location = useLocation();
  const encodedIncidentId = incidentId ? encodeURIComponent(incidentId) : null;
  return (
    <Navigate
      to={{ pathname: encodedIncidentId ? `/app/incidents/${encodedIncidentId}` : '/app/incidents', search: location.search }}
      replace
      state={location.state}
    />
  );
};

export const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<AuthExperience />} />
      <Route path="/accept-invite/:inviteToken" element={<AcceptInvitePage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/orgs/select" element={<OrgSelectPage />} />
      <Route path="/sso/callback" element={<SsoCallbackPage />} />
      <Route path="/mock/sso/:mode" element={<MockSsoPage />} />
      <Route path="/mock/idp/:mode" element={<MockSsoPage />} />
      <Route path="/mock/topology-runtime-overlays" element={<TopologyRuntimeOverlayMockPage />} />
      <Route path="/internal/topology-render/:jobId" element={<InternalTopologyRenderPage />} />
      <Route path="/orgs/create" element={<OrgCreatePage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/slack/connect" element={<SlackConnectPage />} />
      <Route path="/integrations/connect" element={<IntegrationConnectPage />} />
      <Route path="/app" element={<ShellPage />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<OrgOverviewPage />} />
        <Route path="incidents" element={<UnifiedIncidentsPage />} />
        <Route path="incidents/:incidentId" element={<UnifiedIncidentPage />} />
        <Route path="incidents-v2" element={<LegacyIncidentsListRedirect />} />
        <Route path="incidents-v2/:incidentId" element={<LegacyIncidentDetailRedirect />} />
        <Route path="grafana" element={<GrafanaExplorerPage />} />
        <Route path="ping-mesh" element={<PingMeshPage />} />
        <Route path="flows" element={<FlowAnalyticsPage />} />
        <Route path="people-access" element={<AccessIdentityPage />} />
        <Route path="connectivity" element={<ConnectivitySitesPage />} />
        <Route path="posture" element={<PosturePage />} />
        <Route path="posture/findings/:runId/:findingKey" element={<PosturePage />} />
        <Route path="topology-v2" element={<TopologyV2Page />} />
        <Route path="path-tracer" element={<TopologyPathTracerPage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="endpoints" element={<EndpointsPage />} />
        <Route path="cve" element={<CVEGlobalSummaryPage />} />
        <Route path="troubleshoot" element={<TroubleshootPage />} />
        <Route path="inventory/:deviceId/ssh" element={<DeviceSshPage />} />
        <Route path="inventory/:deviceId/running-config" element={<DeviceRunningConfigPage />} />
        <Route path="sites/:siteId/intelligence" element={<SiteIntelligencePage />} />
        <Route path="credentials" element={<CredentialsPage />} />
        <Route path="integrations" element={<IntegrationsPage />} />
        <Route path="chat-agents" element={<ChatAgentsPage />} />
        <Route path="changes" element={<ChangeManagementPage />} />
        <Route path="runbooks" element={<Navigate to="library" replace />} />
        <Route path="runbooks/library" element={<RunbooksPage />} />
        <Route path="runbooks/schedules" element={<RunbooksPage />} />
        <Route path="runbooks/executions" element={<RunbooksPage />} />
        <Route path="runbooks/executions/:instanceId" element={<ExecutionPage />} />
        <Route path="runbooks/new" element={<RunbookNewPage />} />
        <Route path="runbooks/:runbookId" element={<RunbookDetailPage />} />
        <Route path="knowledge" element={<KnowledgeUploadPage />} />
        <Route path="api-docs" element={<ApiDocsPage />} />
        <Route path="audit-activity" element={<AuditActivityPage />} />
        <Route path="settings" element={<OrgSettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRoutes;
